import { AitCellData } from "./AitCellData";
import { AitRowData } from "./AitRowData";
import {
  AitHeaderCellData,
  AitHeaderRowData,
  AitLocation,
  AitLocationBodyCell,
  AitLocationHeaderCell,
  AitTableData,
  AitTablePosition,
} from "./interface";

/**
 * Utility to locate a cell object inside a collection of rows.
 * Works for both header rows and body rows and optionally restricts by row/column indices
 * to support repeated rows or columns.
 *
 * @example
 * ```typescript
 * const cell = findCellInRows(rows, 'row-1', 'cell-2', 0, 1);
 * ```
 */
type cellReturn<T> = AitHeaderCellData<T> | AitCellData<T> | undefined;
export function findCellInRows<T>(
  rows: AitHeaderRowData<T>[],
  rowId: string,
  cellId: string,
  rowIndex?: number,
  columnIndex?: number,
  ignoreRepeats?: boolean,
): AitHeaderCellData<T> | undefined;
export function findCellInRows<T>(
  rows: AitRowData<T>[],
  rowId: string,
  cellId: string,
  rowIndex?: number,
  columnIndex?: number,
  ignoreRepeats?: boolean,
): AitCellData<T> | undefined;
/**
 * Find a cell in the provided rows matching the supplied identifiers.
 *
 * @param rows - Array of rows (header or body) to search
 * @param rowId - The `aitid` of the target row
 * @param cellId - The `aitid` of the target cell
 * @param rowIndex - Optional index of the row to match (used when there are repeated rows)
 * @param columnIndex - Optional index of the cell column to match (used when there are repeated columns)
 * @param ignoreRepeats - When true, only the `rowId` and `cellId` are used for matching and indices are ignored
 * @returns The found cell object (`AitHeaderCellData` or `AitCellData`) or `undefined` when not found
 */
export function findCellInRows<T>(
  rows: AitRowData<T>[] | AitHeaderRowData<T>[],
  rowId: string,
  cellId: string,
  rowIndex?: number,
  columnIndex?: number,
  ignoreRepeats?: boolean,
): cellReturn<T> {
  return rows
    .find((row, ri) => row.aitid === rowId && (ignoreRepeats || rowIndex === ri))
    ?.cells.find((cell, ci) => cell.aitid === cellId && (ignoreRepeats || columnIndex === ci));
}

/**
 * Resolve a single cell from a table data structure using a location descriptor.
 *
 * @param tableData - Table data (headerData and bodyData) to search
 * @param location - Location descriptor identifying a header or body cell
 * @param ignoreRepeats - When true, ignores row/column indices and matches only by ids
 * @returns The located cell (`AitHeaderCellData` or `AitCellData`) or `undefined` if not found
 */
export function trGetCellFromLocation<T>(
  tableData: AitTableData<T>,
  location: AitLocationHeaderCell,
  ignoreRepeats?: boolean,
): AitHeaderCellData<T> | undefined;
export function trGetCellFromLocation<T>(
  tableData: AitTableData<T>,
  location: AitLocationBodyCell,
  ignoreRepeats?: boolean,
): AitCellData<T> | undefined;
export function trGetCellFromLocation<T>(
  tableData: AitTableData<T>,
  location: AitLocationHeaderCell | AitLocationBodyCell,
  ignoreRepeats?: boolean,
): AitHeaderCellData<T> | AitCellData<T> | undefined;
export function trGetCellFromLocation<T>(
  tableData: AitTableData<T>,
  location: AitLocation,
  ignoreRepeats?: boolean,
): cellReturn<T> {
  if (location.position === AitTablePosition.headerCell && tableData?.headerData) {
    return findCellInRows(
      tableData.headerData.rows,
      location.rowId,
      location.cellId,
      location.row,
      location.column,
      ignoreRepeats,
    );
  }

  if (location.position === AitTablePosition.bodyCell && tableData?.bodyData) {
    const rowGroup = tableData.bodyData[location.rowGroup];
    /* istanbul ignore else */
    if (rowGroup) {
      return findCellInRows(
        rowGroup.rows,
        location.rowId,
        location.cellId,
        location.row,
        location.column,
        ignoreRepeats,
      );
    }
  }

  return undefined;
}
